import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { T, THEMES } from "../themes";
import { ArrowClockwise, Funnel, WarningCircle } from "@phosphor-icons/react";
import AppHeader from "../components/AppHeader";

// Apply Balanced (light) theme — standalone page, T is not pre-populated by App render cycle.
Object.assign(T, THEMES.Balanced || THEMES.Critical);

// Local aliases — resolved once from T (which was populated above via Object.assign).
const TEAL        = T.accent;
const PAGE_BG     = T.pageBg;
const CARD_BG     = T.card;
const CARD_SHADOW = T.shadowXs;
const TEXT        = T.ink;
const TEXT_SUB    = T.textSub;
const TEXT_DIM    = T.textDim;
const INPUT_BG    = T.surfaceLight;
const BORDER      = `1px solid ${T.cardBorder}`;
const FONT_SANS   = T.fontSans;
const FONT_HEAD   = T.fontDisplay;
const FONT_MONO   = T.fontMono;
const RED         = T.danger;

// ── Category badge config ─────────────────────────────────────────────────────
// TODO: tokenise badge colours when badge colour tokens added to themes.js
const CAT = {
  bug:     { label: "Bug",             bg: "#e24b4a18", color: "#c0392b", border: "#e24b4a44" },
  feature: { label: "Feature Request", bg: "#2196b818", color: "#1a6b8a", border: "#2196b844" },
  food:    { label: "Food / Exercise", bg: "#f9c74f22", color: "#8a6200", border: "#f9c74f66" },
  ui:      { label: "UI Feedback",     bg: "#88888818", color: "#555555", border: "#88888844" },
};

// TODO: tokenise severity colours when badge colour tokens added to themes.js
const SEV = {
  low:      { label: "Low",      bg: "#88888815", color: "#666666" },
  medium:   { label: "Medium",   bg: "#f9c74f20", color: "#8a6200" },
  high:     { label: "High",     bg: "#f9730020", color: "#c05500" },
  critical: { label: "Critical", bg: "#e24b4a20", color: "#c0392b" },
};

const APP_LABELS = {
  nutrilithe: "NutriLithe",
  liftlithe:  "LiftLithe",
  both:       "Both",
};

const ALL = "all";


function Badge({ cfg, text }) {
  if (!cfg) return null;
  return (
    <span style={{
      display: "inline-block",
      background: cfg.bg,
      color: cfg.color,
      border: `1px solid ${cfg.border || cfg.color + "44"}`,
      borderRadius: 6,
      padding: "2px 8px",
      fontSize: 11,
      fontWeight: 700,
      fontFamily: FONT_SANS,
      letterSpacing: "0.3px",
      whiteSpace: "nowrap",
    }}>
      {text || cfg.label}
    </span>
  );
}

function FilterBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? TEAL : CARD_BG,
        color: active ? "#ffffff" : TEXT,
        border: active ? "1.5px solid transparent" : BORDER,
        borderRadius: T.radiusXs,
        padding: "6px 12px",
        fontSize: 12,
        fontWeight: active ? 700 : 500,
        fontFamily: FONT_SANS,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 0.15s",
        boxShadow: active ? "none" : CARD_SHADOW,
      }}
    >
      {children}
    </button>
  );
}

function FeedbackCard({ row }) {
  const cat = CAT[row.category] || CAT.ui;
  const sev = row.severity ? SEV[row.severity] : null;
  const date = new Date(row.created_at);
  const dateStr = date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  return (
    <div style={{
      background: CARD_BG,
      border: BORDER,
      borderRadius: T.radius,
      padding: "16px 20px",
      marginBottom: 12,
      boxShadow: CARD_SHADOW,
    }}>
      {/* Top row: badges + meta */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <Badge cfg={cat} />
          {sev && <Badge cfg={sev} text={sev.label} />}
          <span style={{
            display: "inline-block",
            background: INPUT_BG,
            color: TEXT_SUB,
            border: BORDER,
            borderRadius: 6,
            padding: "2px 8px",
            fontSize: 11,
            fontWeight: 600,
            fontFamily: FONT_SANS,
          }}>
            {APP_LABELS[row.app] || row.app || "—"}
          </span>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ color: TEXT, fontFamily: FONT_SANS, fontSize: 12, fontWeight: 600 }}>{dateStr}</div>
          <div style={{ color: TEXT_DIM, fontFamily: FONT_SANS, fontSize: 11 }}>{timeStr}</div>
        </div>
      </div>

      {/* Description */}
      <p style={{
        color: TEXT,
        fontFamily: FONT_SANS,
        fontSize: 14,
        lineHeight: 1.6,
        margin: "0 0 10px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}>
        {row.description}
      </p>

      {/* Footer: user ID + device */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", borderTop: BORDER, paddingTop: 10, marginTop: 4 }}>
        <span style={{ color: TEXT_DIM, fontFamily: FONT_MONO, fontSize: 11 }}>
          uid: {row.user_id ? row.user_id.slice(0, 8) : "—"}
        </span>
        {row.device_info && (
          <span style={{ color: TEXT_DIM, fontFamily: FONT_SANS, fontSize: 11 }}>
            📱 {row.device_info}
          </span>
        )}
      </div>
    </div>
  );
}

export default function AdminFeedbackPage() {
  const [authState, setAuthState] = useState("loading"); // loading | unauthed | not_admin | ready
  const [rows, setRows]           = useState([]);
  const [fetching, setFetching]   = useState(false);
  const [fetchErr, setFetchErr]   = useState("");
  const [catFilter, setCatFilter] = useState(ALL);
  const [appFilter, setAppFilter] = useState(ALL);

  const loadFeedback = useCallback(async () => {
    setFetching(true);
    setFetchErr("");
    const { data, error } = await supabase
      .from("feedback")
      .select("*")
      .order("created_at", { ascending: false });
    setFetching(false);
    if (error) { setFetchErr(error.message); return; }
    setRows(data || []);
  }, []);

  useEffect(() => {
    async function init() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setAuthState("unauthed"); return; }

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("id", session.user.id)
        .single();

      if (error || !profile?.is_admin) { setAuthState("not_admin"); return; }

      setAuthState("ready");
      loadFeedback();
    }
    init();
  }, [loadFeedback]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (authState === "loading") {
    return (
      <div style={pageStyle}>
        <AppHeader title="Admin" subtitle="Feedback management" currentApp="nutri" />
        <div style={{ color: TEXT_DIM, fontFamily: FONT_SANS, fontSize: 14, padding: 48, textAlign: "center" }}>
          Loading…
        </div>
      </div>
    );
  }

  // ── Not authed / not admin ─────────────────────────────────────────────────
  if (authState === "unauthed" || authState === "not_admin") {
    return (
      <div style={pageStyle}>
        <AppHeader title="Admin" subtitle="Feedback management" currentApp="nutri" />
        <div style={{ padding: 48, textAlign: "center", color: TEXT_DIM, fontFamily: FONT_SANS, fontSize: 14 }}>
          Not authorised.
        </div>
      </div>
    );
  }

  // ── Admin view ─────────────────────────────────────────────────────────────
  const visible = rows.filter(r =>
    (catFilter === ALL || r.category === catFilter) &&
    (appFilter === ALL || r.app === appFilter)
  );

  const catCounts = Object.fromEntries(
    ["bug","feature","food","ui"].map(c => [c, rows.filter(r => r.category === c).length])
  );

  return (
    <div style={pageStyle}>
      <AppHeader title="Admin" subtitle="Feedback management" currentApp="nutri" />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 0 60px" }}>

        {/* Page header */}
        <div style={{
          padding: "24px 24px 20px",
          borderBottom: BORDER,
          marginBottom: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}>
          <div>
            <h1 style={{ color: TEXT, fontFamily: FONT_HEAD, fontSize: 22, fontWeight: 800, margin: 0 }}>
              Feedback
            </h1>
            <p style={{ color: TEXT_DIM, fontFamily: FONT_SANS, fontSize: 12, margin: "4px 0 0" }}>
              {rows.length} total · {visible.length} shown
            </p>
          </div>
          <button
            onClick={loadFeedback}
            disabled={fetching}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: CARD_BG, color: TEXT_SUB, border: BORDER,
              borderRadius: T.radiusXs, padding: "7px 14px",
              fontSize: 12, fontWeight: 600, fontFamily: FONT_SANS,
              cursor: fetching ? "default" : "pointer",
              opacity: fetching ? 0.5 : 1,
              boxShadow: CARD_SHADOW,
            }}
          >
            <ArrowClockwise size={13} style={{ animation: fetching ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div style={{ padding: "0 24px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <Funnel size={13} color={TEXT_DIM} />
            <span style={{ color: TEXT_DIM, fontFamily: FONT_SANS, fontSize: 11, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", marginRight: 4 }}>
              Category
            </span>
            <FilterBtn active={catFilter === ALL} onClick={() => setCatFilter(ALL)}>
              All ({rows.length})
            </FilterBtn>
            {["bug","feature","food","ui"].map(c => (
              <FilterBtn key={c} active={catFilter === c} onClick={() => setCatFilter(c)}>
                {CAT[c].label} ({catCounts[c]})
              </FilterBtn>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Funnel size={13} color={TEXT_DIM} />
            <span style={{ color: TEXT_DIM, fontFamily: FONT_SANS, fontSize: 11, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", marginRight: 4 }}>
              App
            </span>
            {[ALL, "nutrilithe", "liftlithe", "both"].map(a => (
              <FilterBtn key={a} active={appFilter === a} onClick={() => setAppFilter(a)}>
                {a === ALL ? "All" : APP_LABELS[a]}
              </FilterBtn>
            ))}
          </div>
        </div>

        {/* Error */}
        {fetchErr && (
          <div style={{
            margin: "0 24px 16px",
            display: "flex", alignItems: "flex-start", gap: 8,
            background: `${RED}18`, border: `1px solid ${RED}44`,
            borderRadius: T.radiusXs, padding: "10px 14px",
          }}>
            <WarningCircle size={15} color={RED} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ color: RED, fontFamily: FONT_SANS, fontSize: 13 }}>{fetchErr}</span>
          </div>
        )}

        {/* Cards */}
        <div style={{ padding: "0 24px" }}>
          {visible.length === 0 && !fetching && (
            <p style={{ color: TEXT_DIM, fontFamily: FONT_SANS, fontSize: 14, textAlign: "center", padding: "32px 0" }}>
              {rows.length === 0 ? "No feedback yet." : "No results for this filter."}
            </p>
          )}
          {visible.map(row => <FeedbackCard key={row.id} row={row} />)}
        </div>

      </div>

      {/* Spin keyframe injected once */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  backgroundColor: PAGE_BG,
  fontFamily: FONT_SANS,
};
